import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import OwnerReassignControl, { useProjectOwnership } from '../components/OwnerReassignControl';
import { useOwnerDirectory } from '../hooks/useOwnerDirectory';
// AOQ-5 (#2921, §2.6) — phase badge/filter so an Architecture-phase
// (SAD-sourced) question is visually distinguishable from a PRD-phase one
// within this project's single combined list. Reuses the SAME PhaseBadge
// component and canonical phase vocabulary RisksPage.tsx already uses for
// its own phase column/filter (#2611) — one source of phase display, not a
// second divergent copy.
import PhaseBadge, { type PhaseKey } from '../components/PhaseBadge';
import { CANONICAL_PHASES, CANONICAL_PHASE_LABELS, isCanonicalPhase } from '@blueprint/shared';

const PHASE_FILTER_OPTIONS = CANONICAL_PHASES.map((key) => ({ value: key, label: CANONICAL_PHASE_LABELS[key] }));

// #3165 — sentinel <option> value for "Other…" in the escalation-owner
// select. Never a real owner name (project member / stakeholder names are
// validated non-empty, human-entered text; this string is reserved).
const ESCALATION_OWNER_OTHER_VALUE = '__escalation_owner_other__';

// #2057 item 2 — the resolve/defer UI GateReportPanel's dead
// /projects/:projectId/open-questions link has pointed at since the gate
// criterion shipped (PROTO.A7, #1625). No create path: questions arrive via
// PRD extraction (reconcileOpenQuestions), not manual entry — a separate,
// undecided product question (see #2057's item-1 discussion).

// #2497 (Model A) — the agent's unreviewed proposal for a still-open
// question, merged into the list response by the GET route (never a
// separate fetch — see routes/open-questions.ts). null for a question with
// no pending proposal.
interface OpenQuestionProposal {
  id: string;
  category: 'agent_can_resolve' | 'needs_stakeholder' | 'cross_app_defer';
  categoryDetail: string | null;
  proposedResolution: string | null;
  recommendDefer: boolean;
  deferReason: string | null;
  basis: string | null;
  impliedFrEdits: { frId: string; proposedText: string }[];
}

interface OpenQuestion {
  id: string;
  seq: string;
  question: string;
  status: 'open' | 'resolved' | 'deferred' | 'escalated';
  deferReason: string | null;
  escalationOwner: string | null;
  // #2846 — the accountable project member (#2741's ownership model),
  // distinct from escalationOwner's free-text routing note.
  ownerId: string | null;
  resolution: string | null;
  resolvedBy: string | null;
  resolvedByName: string | null;
  resolvedAt: string | null;
  createdAt: string;
  // AOQ-5 (#2921) — the phase/artefact this question was sourced from
  // (openQuestionsService.ts's listOpenQuestions). Null for a legacy
  // pre-AOQ-0 row never backfilled.
  phaseId: string | null;
  proposal: OpenQuestionProposal | null;
  // #2539 — true when a PENDING OQ-sourced prd_refinement_suggestions row
  // exists for this question (the §9.2 row and/or an FR-update offer). For
  // a 'resolved' question this means a legacy row predating #2497/#2508's
  // one-Accept fix is stuck with no forward-facing accept path — the
  // "Apply pending suggestion" affordance below clears it.
  hasPendingSuggestion: boolean;
}

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  open:      { bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)' },
  resolved:  { bg: 'rgba(16,185,129,0.12)', color: 'var(--success)' },
  deferred:  { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
  escalated: { bg: 'rgba(99,102,241,0.12)', color: 'var(--primary)' },
};

const CATEGORY_LABEL: Record<OpenQuestionProposal['category'], string> = {
  agent_can_resolve: 'Agent can resolve',
  needs_stakeholder: 'Needs stakeholder',
  cross_app_defer: 'Cross-app dependency',
};

function Badge({ text, style }: { text: string; style: { bg: string; color: string } }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
      background: style.bg, color: style.color, letterSpacing: '0.02em',
      textTransform: 'uppercase',
    }}>
      {text}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function OpenQuestionsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const ownership = useProjectOwnership(projectId);
  // #3165 — named-party suggestions for the escalation "Owner" field
  // (distinct from `ownership` above, which backs the ownerId reassign
  // control, a closed set of platform accounts).
  const ownerDirectory = useOwnerDirectory(projectId);

  const [questions, setQuestions] = useState<OpenQuestion[]>([]);
  const [loading, setLoading]     = useState(true);
  // #2370 GAP 1 — resolving a question doesn't itself flow the answer to
  // the PRD (that needs a separate capture-scan + accept, on
  // RefinementSuggestionsPage). unincorporatedCount makes that follow-up
  // step discoverable instead of leaving it to be found by accident.
  // Best-effort: a failed fetch just leaves the banner hidden, never blocks
  // the page's own load/error state.
  const [unincorporatedCount, setUnincorporatedCount] = useState(0);
  // #2057 R2 review finding — `loading` alone was reused for both the
  // initial fetch AND every post-action refetch, so resolving/deferring any
  // single row blanked the entire list (including unrelated rows) behind a
  // bare "Loading…" line until the refetch round-trip completed. hasLoaded
  // gates the full-page loading state to the FIRST fetch only; a refresh
  // keeps the current (soon-to-be-stale, about-to-be-replaced) list visible.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError]         = useState<string | null>(null);

  // Per-row action state
  const [actingId, setActingId]         = useState<string | null>(null);
  const [actionError, setActionError]   = useState<Record<string, string>>({});
  const [deferringId, setDeferringId]   = useState<string | null>(null);
  const [deferReasonDraft, setDeferReasonDraft] = useState('');
  // #2103 — mirrors deferringId/deferReasonDraft exactly.
  const [resolvingId, setResolvingId]   = useState<string | null>(null);
  const [resolutionDraft, setResolutionDraft] = useState('');
  // #2497 — mirrors deferringId/deferReasonDraft exactly (Decision 2:
  // escalate needs a named owner, same shape as defer needing a reason).
  const [escalatingId, setEscalatingId]         = useState<string | null>(null);
  const [escalationOwnerDraft, setEscalationOwnerDraft] = useState('');
  // #3165 — true shows the free-text "Other" input for escalationOwnerDraft,
  // false shows the directory <select>. Single scalar, same convention as
  // escalatingId/escalationOwnerDraft above (only one row escalates at a
  // time).
  const [escalationOwnerOtherMode, setEscalationOwnerOtherMode] = useState(false);
  const [generatingProposals, setGeneratingProposals] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  // AOQ-5 (#2921) — phase filter, same "empty string = All" convention as
  // RisksPage.tsx's own filterPhase.
  const [filterPhase, setFilterPhase] = useState('');

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    apiFetch<OpenQuestion[]>(`/projects/${projectId}/open-questions`)
      .then(rows => { setQuestions(rows); setError(null); })
      .catch(e => setError((e as Error).message))
      .finally(() => { setLoading(false); setHasLoaded(true); });
  }, [projectId]);

  const loadUnincorporatedCount = useCallback(() => {
    if (!projectId) return;
    apiFetch<{ count: number }>(`/projects/${projectId}/open-questions/unincorporated-count`)
      .then(r => setUnincorporatedCount(r.count))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => { load(); loadUnincorporatedCount(); }, [load, loadUnincorporatedCount]);

  async function patchStatus(q: OpenQuestion, status: 'resolved' | 'deferred' | 'escalated', deferReason?: string, resolution?: string, escalationOwner?: string) {
    if (!projectId) return;
    setActingId(q.id);
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
    try {
      const body =
        status === 'deferred' ? { status, deferReason }
        : status === 'escalated' ? { status, escalationOwner }
        : { status, resolution };
      await apiFetch(`/projects/${projectId}/open-questions/${q.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      // #2491 R2 (codex) finding — resolvingId/resolutionDraft and
      // deferringId/deferReasonDraft are single, row-agnostic scalars, not
      // per-row state. A DIFFERENT row's Edit/Resolve/Defer button is never
      // disabled by THIS row's in-flight PATCH (only actingId===its-own-id
      // disables a row's own button), so while this await is pending, the
      // user can open an edit on another row entirely. Clearing these
      // unconditionally here would silently discard that other row's
      // not-yet-submitted draft the instant this row's own, unrelated save
      // completes. Only close/clear them if they still belong to THIS row —
      // checked via the functional-updater form against the LATEST state,
      // not this closure's (possibly now-stale) resolvingId/deferringId.
      let closedOwnResolve = false;
      setResolvingId(prev => {
        if (prev !== q.id) return prev;
        closedOwnResolve = true;
        return null;
      });
      if (closedOwnResolve) setResolutionDraft('');

      let closedOwnDefer = false;
      setDeferringId(prev => {
        if (prev !== q.id) return prev;
        closedOwnDefer = true;
        return null;
      });
      if (closedOwnDefer) setDeferReasonDraft('');

      let closedOwnEscalate = false;
      setEscalatingId(prev => {
        if (prev !== q.id) return prev;
        closedOwnEscalate = true;
        return null;
      });
      if (closedOwnEscalate) { setEscalationOwnerDraft(''); setEscalationOwnerOtherMode(false); }

      load();
      // A fresh resolve is exactly the case that grows this count — refresh
      // it alongside the list rather than waiting for the next full page load.
      if (status === 'resolved') loadUnincorporatedCount();
    } catch (e) {
      setActionError(prev => ({ ...prev, [q.id]: (e as Error).message }));
    } finally {
      // Same cross-row hazard as above — a slower row's finally must not
      // clear actingId out from under a different row's own action.
      setActingId(prev => (prev === q.id ? null : prev));
    }
  }

  function startDefer(q: OpenQuestion) {
    setDeferringId(q.id);
    setDeferReasonDraft('');
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
  }

  function cancelDefer() {
    setDeferringId(null);
    setDeferReasonDraft('');
  }

  // #2103 — mirrors startDefer/cancelDefer exactly.
  // #2491 — also reused to EDIT an already-resolved row's resolution:
  // prefilling with q.resolution (null on a fresh 'open' row, the existing
  // text on a 'resolved' row) turns the same textarea into an edit form with
  // no new component. Saving re-PATCHes the same endpoint with the new text,
  // which openQuestionsService.ts's setOpenQuestionStatus already audits
  // unconditionally and refinementSuggestionService.ts's
  // captureOpenQuestionSuggestion already re-captures automatically when the
  // resolution content changed — no new wiring needed for either.
  function startResolve(q: OpenQuestion) {
    setResolvingId(q.id);
    // #2497 — prefill from the pending proposal's suggested wording when one
    // exists (this is also the Modify entry point for a proposal), else the
    // existing resolution (editing an already-resolved row, #2491), else blank.
    setResolutionDraft(q.proposal?.proposedResolution ?? q.resolution ?? '');
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
  }

  function cancelResolve() {
    setResolvingId(null);
    setResolutionDraft('');
  }

  // #2497 — mirrors startDefer/cancelDefer exactly. Prefills from the
  // proposal's own suggested owner/party when one exists (Modify-before-
  // escalate is the same "prefill, let the founder edit" pattern as
  // startResolve above), blank otherwise.
  function startEscalate(q: OpenQuestion) {
    const prefill = q.proposal?.categoryDetail ?? '';
    setEscalatingId(q.id);
    setEscalationOwnerDraft(prefill);
    // #3165 — a prefilled value absent from the current directory (the
    // agent proposed a party no longer/never on the list) must still show
    // as editable text, not silently vanish into an unselected <select>.
    setEscalationOwnerOtherMode(
      prefill !== '' && !ownerDirectory.entries.some((e) => e.key === prefill),
    );
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
  }

  function cancelEscalate() {
    setEscalatingId(null);
    setEscalationOwnerDraft('');
    setEscalationOwnerOtherMode(false);
  }

  // #2497 (Model A) — the ONE-Accept action. `finalResolution` present means
  // Modify (the founder edited the proposed text in the same textarea
  // startResolve/startModifyProposal opens); omitted means Accept the
  // proposal's own wording verbatim. Writes BOTH the open_questions record
  // and the PRD/FR splice server-side in one request — see
  // openQuestionProposalService.ts's acceptProposal.
  async function acceptProposalAction(q: OpenQuestion, finalResolution?: string) {
    if (!projectId || !q.proposal) return;
    setActingId(q.id);
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
    try {
      await apiFetch(`/projects/${projectId}/open-questions/${q.id}/accept-proposal`, {
        method: 'POST',
        body: JSON.stringify({ proposalId: q.proposal.id, finalResolution: finalResolution?.trim() || undefined }),
      });
      let closedOwnResolve = false;
      setResolvingId(prev => {
        if (prev !== q.id) return prev;
        closedOwnResolve = true;
        return null;
      });
      if (closedOwnResolve) setResolutionDraft('');
      load();
    } catch (e) {
      setActionError(prev => ({ ...prev, [q.id]: (e as Error).message }));
    } finally {
      setActingId(prev => (prev === q.id ? null : prev));
    }
  }

  // #2539 — clears a legacy pending OQ-sourced suggestion on an already-
  // resolved question. Server-side this calls
  // applyPendingSuggestionForResolvedQuestion, which reuses
  // spliceResolutionIntoPrd directly (NOT the PATCH route's
  // resolveOpenQuestionManually) — no resolution/resolvedBy/resolvedAt
  // change, only the PRD splice + the suggestion row's own status.
  async function applyPendingSuggestionAction(q: OpenQuestion) {
    if (!projectId) return;
    setActingId(q.id);
    setActionError(prev => { const next = { ...prev }; delete next[q.id]; return next; });
    try {
      await apiFetch(`/projects/${projectId}/open-questions/${q.id}/apply-pending-suggestion`, {
        method: 'POST',
      });
      load();
      // R1 (blueprint-reviewer) finding — mirrors patchStatus's own
      // 'resolved' branch: a question this affordance clears was, by
      // definition, still counted as unincorporated (see
      // listUnincorporatedResolvedOpenQuestions) until this call folds its
      // resolution into the PRD. Without this, the banner over-reports
      // until the next full page load.
      loadUnincorporatedCount();
    } catch (e) {
      setActionError(prev => ({ ...prev, [q.id]: (e as Error).message }));
    } finally {
      setActingId(prev => (prev === q.id ? null : prev));
    }
  }

  // Shared Save handler for the resolve textarea (startResolve's UI serves
  // BOTH a plain manual resolve and a Modify-then-accept of a pending
  // proposal) — routes to whichever write path applies, rather than
  // duplicating the textarea/buttons per case.
  function saveResolve(q: OpenQuestion) {
    if (q.proposal) {
      void acceptProposalAction(q, resolutionDraft);
    } else {
      void patchStatus(q, 'resolved', undefined, resolutionDraft.trim());
    }
  }

  async function handleGenerateProposals() {
    if (!projectId) return;
    setGeneratingProposals(true);
    setProposalError(null);
    try {
      await apiFetch(`/projects/${projectId}/open-questions/generate-proposals`, { method: 'POST' });
      load();
    } catch (e) {
      setProposalError((e as Error).message);
    } finally {
      setGeneratingProposals(false);
    }
  }

  const btnPrimary: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
    background: 'var(--primary)', color: '#fff', border: 'none', cursor: 'pointer',
  };

  const btnGhost: React.CSSProperties = {
    ...btnPrimary, background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  };

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '18px 20px',
  };

  const selectStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, padding: '6px 10px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
  };

  // AOQ-5 (#2921, §2.6) — the filtered view: Architecture-phase items
  // distinguishable from PRD-phase items within this project's single
  // combined list, same predicate shape as RisksPage.tsx's own filterPhase.
  const filteredQuestions = filterPhase
    ? questions.filter(q => q.phaseId === filterPhase)
    : questions;

  return (
    <main style={{ flex: 1, overflow: 'auto', padding: '32px 40px', maxWidth: 900 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/projects/${projectId}`}
          style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
        >
          ← Dashboard
        </Link>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginTop: 6 }}>
          Open Questions
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          Resolve or defer every tracked question to clear the Phase 1 exit gate.
        </p>
      </div>

      {/* #2497 (Model A) — on demand, not automatic: an LLM call per run,
          same "explicit trigger" convention as the old refinement-suggestions
          capture scan. */}
      {questions.some(q => q.status === 'open') && (
        <div style={{ marginBottom: 20 }}>
          <button
            onClick={() => void handleGenerateProposals()}
            disabled={generatingProposals}
            style={{
              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
              padding: '6px 14px', borderRadius: 'var(--radius-sm)',
              background: 'transparent', color: 'var(--primary)',
              border: '1px solid var(--primary)', cursor: 'pointer',
              opacity: generatingProposals ? 0.6 : 1,
            }}
          >
            {generatingProposals ? 'Generating…' : 'Generate agent proposals'}
          </button>
          {proposalError && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 6 }}>{proposalError}</p>
          )}
        </div>
      )}

      {/* #2370 GAP 1 — resolving a question only records the answer; it does
          NOT itself reach the PRD. Surface that follow-up step instead of
          leaving it silently undiscoverable. */}
      {unincorporatedCount > 0 && (
        <Link
          to={`/projects/${projectId}/refinement-suggestions`}
          style={{
            display: 'block', marginBottom: 20, padding: '10px 14px',
            borderRadius: 'var(--radius-sm)', background: 'rgba(245,158,11,0.1)',
            textDecoration: 'none',
          }}
        >
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--warning)', margin: 0 }}>
            <strong>{unincorporatedCount}</strong> resolved answer{unincorporatedCount !== 1 ? 's' : ''} ready to fold into the PRD → Generate suggestions
          </p>
        </Link>
      )}

      {/* AOQ-5 (#2921, §2.6) — phase filter, shown once there's more than
          one project phase's worth of tracked items to distinguish (same
          "don't show a control with nothing to filter" convention RisksPage
          uses for its carried-forward-to filter, #2620). */}
      {questions.some(q => q.phaseId && q.phaseId !== questions[0]?.phaseId) || filterPhase ? (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selectStyle}>
            <option value="">All Phases</option>
            {PHASE_FILTER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterPhase && (
            <button onClick={() => setFilterPhase('')} style={{ ...selectStyle, background: 'transparent', color: 'var(--text-secondary)' }}>
              Clear filter
            </button>
          )}
        </div>
      ) : null}

      {loading && !hasLoaded ? (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>
      ) : error ? (
        <p style={{ fontFamily: 'var(--font-body)', color: 'var(--error)', fontSize: 14 }}>{error}</p>
      ) : questions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
            No open questions tracked for this project yet.
          </p>
        </div>
      ) : filteredQuestions.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
            No open questions match the current filter.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filteredQuestions.map(q => (
            <div key={q.id} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Badge text={q.status} style={STATUS_STYLE[q.status] ?? STATUS_STYLE.open} />
                    {/* AOQ-5 (#2921, §2.6) — visually distinguishes an
                        Architecture-phase (SAD-sourced) question from a
                        PRD-phase one within this combined list. Omitted for
                        a legacy row with no phaseId (pre-AOQ-0, never
                        backfilled) rather than rendering a misleading badge. */}
                    {q.phaseId && isCanonicalPhase(q.phaseId) && (
                      <PhaseBadge phase={q.phaseId as PhaseKey} />
                    )}
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)' }}>
                      tracked {formatDate(q.createdAt)}
                    </span>
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', margin: 0, lineHeight: 1.5 }}>
                    {q.question}
                  </p>

                  <OwnerReassignControl
                    projectId={projectId!}
                    entityType="open_question"
                    entityId={q.id}
                    ownerId={q.ownerId}
                    ownership={ownership}
                    onReassigned={(newOwnerId) =>
                      setQuestions(prev => prev.map(x => (x.id === q.id ? { ...x, ownerId: newOwnerId } : x)))}
                  />

                  {q.status === 'deferred' && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--hover, rgba(120,113,108,0.06))' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                        <strong>Deferred{q.resolvedByName ? ` by ${q.resolvedByName}` : ''}:</strong> {q.deferReason}
                      </p>
                    </div>
                  )}

                  {/* #2497 Decision 2 — escalated gates like deferred but is a
                      routing note, not a decision; shown with its owner the
                      same way a defer reason is shown. */}
                  {q.status === 'escalated' && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--hover, rgba(120,113,108,0.06))' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                        <strong>Escalated{q.resolvedByName ? ` by ${q.resolvedByName}` : ''} to {q.escalationOwner}</strong>
                      </p>
                    </div>
                  )}

                  {/* #2497 (Model A) — the agent's grounded proposal, shown
                      inline on the still-open question it's for. Accept/
                      Modify/Defer/Escalate all live in the action buttons
                      below (Resolve opens the SAME textarea prefilled with
                      this text — see startResolve); this card is informational,
                      not itself interactive. Hidden while the resolve
                      textarea for THIS row is open (same "don't show it
                      twice" convention as the #2491 resolved-answer block). */}
                  {q.status === 'open' && q.proposal && resolvingId !== q.id && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.2)' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--primary)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                        Agent proposal — {CATEGORY_LABEL[q.proposal.category]}
                      </p>
                      {q.proposal.proposedResolution ? (
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text)', margin: 0 }}>
                          {q.proposal.proposedResolution}
                        </p>
                      ) : (
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                          Recommends deferring{q.proposal.categoryDetail ? ` to ${q.proposal.categoryDetail}` : ''}{q.proposal.deferReason ? `: ${q.proposal.deferReason}` : ''}
                        </p>
                      )}
                      {q.proposal.basis && (
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 0', fontStyle: 'italic' }}>
                          Basis: “{q.proposal.basis}”
                        </p>
                      )}
                      {q.proposal.impliedFrEdits.length > 0 && (
                        <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                          Also updates: {q.proposal.impliedFrEdits.map(e => e.frId).join(', ')}
                        </p>
                      )}
                      {q.proposal.proposedResolution && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button
                            onClick={() => void acceptProposalAction(q)}
                            disabled={actingId === q.id}
                            style={{ ...btnPrimary, opacity: actingId === q.id ? 0.6 : 1, padding: '4px 10px', fontSize: 11 }}
                          >
                            {actingId === q.id ? 'Saving…' : 'Accept'}
                          </button>
                          <button
                            onClick={() => startResolve(q)}
                            disabled={actingId === q.id}
                            style={{ ...btnGhost, opacity: actingId === q.id ? 0.6 : 1, padding: '4px 10px', fontSize: 11 }}
                          >
                            Modify
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {/* #2491 — hidden while this row's edit textarea is open below, so the
                      stale resolution isn't shown twice (once read-only, once editable). */}
                  {q.status === 'resolved' && resolvingId !== q.id && (
                    <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--hover, rgba(120,113,108,0.06))' }}>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
                        {q.resolution ? (
                          <><strong>Resolved{q.resolvedByName ? ` by ${q.resolvedByName}` : ''}:</strong> {q.resolution}</>
                        ) : (
                          // #2103 R2 (Codex) finding — the migration is additive-only,
                          // no backfill, so a row resolved before this column existed
                          // has resolution=null. Without this branch it would render
                          // "Resolved by X:" with an empty, hanging body.
                          <>Resolved{q.resolvedByName ? ` by ${q.resolvedByName}` : ''}</>
                        )}
                      </p>
                    </div>
                  )}

                  {actionError[q.id] && (
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', marginTop: 8, marginBottom: 0 }}>
                      {actionError[q.id]}
                    </p>
                  )}

                  {deferringId === q.id && (
                    <div style={{ marginTop: 12 }}>
                      <textarea
                        autoFocus
                        value={deferReasonDraft}
                        onChange={e => setDeferReasonDraft(e.target.value)}
                        placeholder="Why is this deferred? Who owns it, and when is it due?"
                        style={{
                          fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 10px', width: '100%',
                          minHeight: 60, resize: 'vertical', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
                          outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => void patchStatus(q, 'deferred', deferReasonDraft.trim())}
                          disabled={!deferReasonDraft.trim() || actingId === q.id}
                          style={{ ...btnPrimary, opacity: (!deferReasonDraft.trim() || actingId === q.id) ? 0.6 : 1 }}
                        >
                          {actingId === q.id ? 'Saving…' : 'Confirm defer'}
                        </button>
                        <button onClick={cancelDefer} style={btnGhost}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {resolvingId === q.id && (
                    <div style={{ marginTop: 12 }}>
                      <textarea
                        autoFocus
                        value={resolutionDraft}
                        onChange={e => setResolutionDraft(e.target.value)}
                        placeholder="What was decided?"
                        style={{
                          fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 10px', width: '100%',
                          minHeight: 60, resize: 'vertical', border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
                          outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => saveResolve(q)}
                          disabled={!resolutionDraft.trim() || actingId === q.id}
                          style={{ ...btnPrimary, opacity: (!resolutionDraft.trim() || actingId === q.id) ? 0.6 : 1 }}
                        >
                          {actingId === q.id ? 'Saving…' : q.proposal ? 'Accept (as modified)' : q.status === 'resolved' ? 'Save changes' : 'Confirm resolve'}
                        </button>
                        <button onClick={cancelResolve} style={btnGhost}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {escalatingId === q.id && (
                    <div style={{ marginTop: 12 }}>
                      {escalationOwnerOtherMode ? (
                        <>
                          <input
                            autoFocus
                            aria-label="Who owns this?"
                            value={escalationOwnerDraft}
                            onChange={e => setEscalationOwnerDraft(e.target.value)}
                            placeholder="Who owns this? (e.g. client sponsor, platform team)"
                            style={{
                              fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 10px', width: '100%',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
                              outline: 'none', boxSizing: 'border-box',
                            }}
                          />
                          {ownerDirectory.entries.length > 0 && (
                            <button
                              type="button"
                              onClick={() => { setEscalationOwnerOtherMode(false); setEscalationOwnerDraft(''); }}
                              style={{ ...btnGhost, marginTop: 6, padding: '2px 8px', fontSize: 11 }}
                            >
                              Choose from list instead
                            </button>
                          )}
                        </>
                      ) : (
                        <select
                          autoFocus
                          aria-label="Who owns this?"
                          value={escalationOwnerDraft}
                          onChange={e => {
                            const v = e.target.value;
                            if (v === ESCALATION_OWNER_OTHER_VALUE) {
                              setEscalationOwnerOtherMode(true);
                              setEscalationOwnerDraft('');
                            } else {
                              setEscalationOwnerDraft(v);
                            }
                          }}
                          style={{
                            fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 10px', width: '100%',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-sm)', background: 'var(--surface)', color: 'var(--text)',
                            outline: 'none', boxSizing: 'border-box',
                          }}
                        >
                          <option value="" disabled>Who owns this?</option>
                          {ownerDirectory.entries.map((e) => (
                            <option key={e.key} value={e.key}>{e.label}</option>
                          ))}
                          <option value={ESCALATION_OWNER_OTHER_VALUE}>Other… (e.g. client sponsor, platform team)</option>
                        </select>
                      )}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button
                          onClick={() => void patchStatus(q, 'escalated', undefined, undefined, escalationOwnerDraft.trim())}
                          disabled={!escalationOwnerDraft.trim() || actingId === q.id}
                          style={{ ...btnPrimary, opacity: (!escalationOwnerDraft.trim() || actingId === q.id) ? 0.6 : 1 }}
                        >
                          {actingId === q.id ? 'Saving…' : 'Confirm escalate'}
                        </button>
                        <button onClick={cancelEscalate} style={btnGhost}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>

                {q.status === 'open' && deferringId !== q.id && resolvingId !== q.id && escalatingId !== q.id && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      onClick={() => startResolve(q)}
                      disabled={actingId === q.id}
                      style={{ ...btnPrimary, opacity: actingId === q.id ? 0.6 : 1 }}
                    >
                      Resolve
                    </button>
                    <button
                      onClick={() => startDefer(q)}
                      disabled={actingId === q.id}
                      style={{ ...btnGhost, opacity: actingId === q.id ? 0.6 : 1 }}
                    >
                      Defer
                    </button>
                    <button
                      onClick={() => startEscalate(q)}
                      disabled={actingId === q.id}
                      style={{ ...btnGhost, opacity: actingId === q.id ? 0.6 : 1 }}
                    >
                      Escalate
                    </button>
                  </div>
                )}

                {/* #2491 — a resolved question has no product path to correct a
                    typo'd/wrong/outdated resolution otherwise. Reuses the exact
                    same resolve textarea (startResolve prefills it with the
                    current resolution) and the exact same PATCH — see that
                    function's own comment for why no new endpoint, audit
                    action, or re-propagation trigger is needed. */}
                {q.status === 'resolved' && resolvingId !== q.id && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                    {/* #2539 — a legacy pending suggestion (captured before
                        #2497/#2508's one-Accept fix) has no forward-facing
                        accept path otherwise; the Refinement-Suggestions
                        panel shows it read-only once the question isn't
                        open any more. Calls applyPendingSuggestionForResolvedQuestion
                        server-side — reuses spliceResolutionIntoPrd directly,
                        not the Edit control's PATCH (that would also
                        overwrite resolvedBy/resolvedAt for a click whose
                        only intent is folding the resolution into the PRD). */}
                    {q.hasPendingSuggestion && (
                      <button
                        onClick={() => void applyPendingSuggestionAction(q)}
                        disabled={actingId === q.id}
                        style={{ ...btnPrimary, opacity: actingId === q.id ? 0.6 : 1 }}
                        title="This question has an outstanding resolution suggestion that was never folded into the PRD. Apply it now."
                      >
                        {actingId === q.id ? 'Applying…' : 'Apply pending suggestion'}
                      </button>
                    )}
                    <button
                      onClick={() => startResolve(q)}
                      disabled={actingId === q.id}
                      style={{ ...btnGhost, opacity: actingId === q.id ? 0.6 : 1 }}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
